import LegalLayout from "@/pages/legal/LegalLayout";
import { LEGAL } from "@/lib/legal";

const S = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-20">
    <h2 className="font-heading text-2xl font-bold tracking-tight text-white mt-10 mb-3">
      {title}
    </h2>
    <div className="space-y-4 text-zinc-300 text-[15px] leading-relaxed">{children}</div>
  </section>
);

export default function CookiePolicy() {
  return (
    <LegalLayout
      testid="cookie-policy"
      title="Cookie Policy"
      subtitle={`How ${LEGAL.brand} uses cookies and local storage to keep the app working.`}
    >
      <p>
        {LEGAL.brand} uses a small number of cookies and browser-storage items to keep you
        signed in and to remember your interface preferences. We do <strong>not</strong> use
        advertising cookies, do <strong>not</strong> load third-party trackers, and do
        <strong> not</strong> sell any behavioural data.
      </p>

      <S id="what-we-store" title="1. What we store on your device">
        <p>The following items are stored in your browser&apos;s local storage — not as
          traditional third-party cookies. They stay on your device until you delete them
          or clear your site data.</p>
        <div className="rounded-md border border-white/10 bg-white/[0.02] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-zinc-400 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2 font-mono-tech">Key</th>
                <th className="text-left px-3 py-2 font-mono-tech">Purpose</th>
                <th className="text-left px-3 py-2 font-mono-tech">Category</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {[
                ["co_token", "Signed-in session token. Removing it signs you out.", "Strictly necessary"],
                ["mo_theme", "Remembers light / dark mode preference.", "Preference"],
                ["mo_sidebar_collapsed", "Remembers whether the sidebar is expanded.", "Preference"],
                ["mo_upsell_count", "Counts how many times you've picked an unaffordable tier so we only show a subtle upgrade hint after 2+ tries.", "Preference"],
              ].map(([k, why, cat]) => (
                <tr key={k} className="text-zinc-300">
                  <td className="px-3 py-2 font-mono-tech text-xs text-amber-300">{k}</td>
                  <td className="px-3 py-2">{why}</td>
                  <td className="px-3 py-2 text-zinc-500">{cat}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </S>

      <S id="third-party" title="2. Third-party cookies">
        <p>
          When you buy credits or a subscription you are redirected to <strong>Stripe</strong>
          {" "}for payment. Stripe sets its own cookies to complete the checkout and prevent
          fraud. Those cookies are governed by{" "}
          <a
            className="text-amber-400 hover:underline"
            href="https://stripe.com/cookies-policy/legal"
            target="_blank" rel="noreferrer"
          >Stripe&apos;s Cookie Policy</a>.
          They are only set on the Stripe checkout page — never on {LEGAL.brand} itself.
        </p>
        <p>We do not embed any analytics or advertising SDKs.</p>
      </S>

      <S id="control" title="3. Controlling &amp; deleting these items">
        <p>Because we only use strictly-necessary and preference storage, there is no cookie banner to accept — the storage described above is only created after you sign in or change a setting yourself.</p>
        <p>You can delete all {LEGAL.brand} local storage at any time:</p>
        <ul className="list-disc list-outside pl-5 space-y-1.5 marker:text-amber-400/60">
          <li>In your browser, open <em>Settings → Privacy → Site data</em> and remove data for this site, <strong>or</strong></li>
          <li>Open the browser console and run <code className="text-amber-300 font-mono-tech text-xs">localStorage.clear()</code>.</li>
        </ul>
        <p>Deleting <code className="text-amber-300 font-mono-tech text-xs">co_token</code> will immediately sign you out.</p>
      </S>

      <S id="questions" title="4. Questions">
        <p>Send us an email at{" "}
          <a className="text-amber-400 hover:underline" href={`mailto:${LEGAL.email}`}>{LEGAL.email}</a>
          {" "}and we&apos;ll get back to you.</p>
      </S>
    </LegalLayout>
  );
}
